import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics } from '@/mocks/comics';

export default function ComicPage() {
  const { id } = useParams<{ id: string }>();
  const comicId = Number(id);

  const comic = useMemo(() => comics.find((c) => c.id === comicId), [comicId]);
  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter(
        (c) =>
          c.id !== comic.id &&
          c.categories.some((cat) => comic.categories.includes(cat))
      )
      .slice(0, 6);
  }, [comic]);

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">作品未找到</h1>
            <p className="text-foreground-500 mb-6">该漫画可能已被移除或链接有误</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '分类浏览', path: '/category/all' },
                { label: comic.title },
              ]}
            />
          </div>
        </div>

        <section className="py-8 md:py-10 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-64 flex-shrink-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 汗汗漫画详情`}
                    title={`${comic.title} - 汗汗漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">作者：{comic.author}</p>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span
                    className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                      comic.status === 'ongoing'
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-accent-100 text-accent-700'
                    }`}
                  >
                    {comic.status === 'ongoing' ? '连载中' : '已完结'}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-foreground-600">
                    <i className="ri-star-fill text-accent-400"></i>
                    {comic.rating} 分
                  </span>
                  <span className="flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-eye-line"></i>
                    {comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万人看过` : `${comic.views}人看过`}
                  </span>
                  <span className="text-xs text-foreground-500">更新于 {comic.updatedAt}</span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-5">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 bg-background-100 text-foreground-600 text-xs rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-lg p-4 mb-6">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2">
                    <i className="ri-file-text-line mr-1.5"></i>作品简介
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {comic.description}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold text-foreground-950 font-heading">
                  <i className="ri-list-check mr-2 text-primary-500"></i>
                  章节列表
                  <span className="text-sm font-normal text-foreground-500 ml-2">
                    共 {comic.chapters.length} 话
                  </span>
                </h2>
                <div className="flex items-center gap-2">
                  <button className="px-3 py-1.5 text-xs bg-background-100 text-foreground-600 rounded-full hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                    正序
                  </button>
                  <button className="px-3 py-1.5 text-xs bg-foreground-950 text-background-50 rounded-full whitespace-nowrap cursor-pointer">
                    倒序
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {comic.chapters.map((chapter) => (
                  <div
                    key={chapter.id}
                    className="flex items-center justify-between p-3 bg-background-100 rounded-lg hover:bg-background-200 transition-colors cursor-pointer group"
                  >
                    <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {chapter.title}
                    </span>
                    <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">
                      {chapter.updatedAt}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {relatedComics.length > 0 && (
              <div className="mt-12">
                <h2 className="text-lg font-bold text-foreground-950 font-heading mb-5">
                  <i className="ri-links-line mr-2 text-accent-500"></i>
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {relatedComics.map((c) => (
                    <ComicCard key={c.id} comic={c} size="small" />
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${comic.title} - 汗汗漫画`,
            author: { '@type': 'Person', name: comic.author },
            datePublished: comic.chapters[0]?.updatedAt,
            dateModified: comic.updatedAt,
            description: comic.description,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://hanhancomic.com/comic/${comic.id}`,
            },
          }),
        }}
      />
    </>
  );
}